import { useState, useEffect, useCallback } from 'react'
import { API_BASE } from '../lib/api'

const CHANNELS = [
  { value: 'EMAIL', label: '📧 Email', hint: 'Requires SMTP setup. Needs subject + body.' },
  { value: 'FCM', label: '🔔 Push (FCM)', hint: 'Firebase Cloud Messaging to app/web users. Needs title + body.' },
  { value: 'WHATSAPP', label: '💬 WhatsApp', hint: 'Meta Cloud API template message. Needs body (maps to {{1}}).' },
]

const STATUS_STYLE = {
  QUEUED:  'bg-yellow-100 text-yellow-800',
  RUNNING: 'bg-blue-100 text-blue-800',
  DONE:    'bg-green-100 text-green-800',
  FAILED:  'bg-red-100 text-red-800',
}

function Progress({ sent, failed, total }) {
  const done = sent + failed
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  return (
    <div className="mt-1">
      <div className="flex justify-between text-xs text-gray-500 mb-0.5">
        <span>{sent} sent · {failed} failed · {total} total</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function CampaignRow({ campaign }) {
  return (
    <div className="rounded-2xl border border-orange-100 bg-white/70 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-semibold text-gray-900 text-sm">{campaign.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {campaign.channel}{campaign.targetCity ? ` · ${campaign.targetCity}` : ' · All cities'} · {new Date(campaign.createdAt).toLocaleString()}
            {campaign.createdBy ? ` · ${campaign.createdBy}` : ''}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[campaign.status] || 'bg-gray-100 text-gray-700'}`}>
          {campaign.status}
        </span>
      </div>
      {campaign.subject && (
        <p className="mt-1 text-xs text-gray-600">Subject: {campaign.subject}</p>
      )}
      <p className="mt-1 text-xs text-gray-600 line-clamp-2">{campaign.body}</p>
      {campaign.totalUsers > 0 && (
        <Progress sent={campaign.sentCount} failed={campaign.failCount} total={campaign.totalUsers} />
      )}
    </div>
  )
}

export default function AdminCampaignPanel({ adminToken }) {
  const [channel, setChannel]     = useState('EMAIL')
  const [name, setName]           = useState('')
  const [subject, setSubject]     = useState('')
  const [title, setTitle]         = useState('')
  const [body, setBody]           = useState('')
  const [targetCity, setTargetCity] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError]   = useState('')
  const [formSuccess, setFormSuccess] = useState('')

  const [campaigns, setCampaigns] = useState([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError]   = useState('')

  const authHeaders = { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' }

  const loadCampaigns = useCallback(async () => {
    setListLoading(true)
    setListError('')
    try {
      const res = await fetch(`${API_BASE}/campaign?limit=50`, { headers: authHeaders })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed to load campaigns.')
      setCampaigns(data.campaigns || [])
    } catch (err) {
      setListError(err.message)
    } finally {
      setListLoading(false)
    }
  }, [adminToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCampaigns() }, [loadCampaigns])

  // Auto-refresh while any campaign is RUNNING
  useEffect(() => {
    const hasRunning = campaigns.some((c) => c.status === 'RUNNING')
    if (!hasRunning) return
    const id = setInterval(loadCampaigns, 5000)
    return () => clearInterval(id)
  }, [campaigns, loadCampaigns])

  async function handleSubmit(e) {
    e.preventDefault()
    setFormError('')
    setFormSuccess('')

    if (!name.trim()) return setFormError('Campaign name is required.')
    if (!body.trim()) return setFormError('Message body is required.')
    if (channel === 'EMAIL' && !subject.trim()) return setFormError('Subject is required for email campaigns.')

    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/campaign`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: name.trim(), channel, subject: subject.trim(), title: title.trim(), body: body.trim(), targetCity: targetCity.trim() || undefined }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed to create campaign.')
      setFormSuccess(`Campaign "${data.campaign.name}" queued (ID: ${data.campaign.id}).`)
      setName(''); setSubject(''); setTitle(''); setBody(''); setTargetCity('')
      loadCampaigns()
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const selectedChannel = CHANNELS.find((c) => c.value === channel)

  return (
    <div className="space-y-6">
      {/* ── Create Form ── */}
      <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
        <h2 className="title-font text-xl font-bold text-gray-900">New Campaign</h2>
        <p className="mt-1 text-sm text-gray-500">
          Messages are queued via Kafka and sent in batches. Use <code className="bg-orange-50 px-1 rounded">{'{name}'}</code> in body/title for personalisation.
        </p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          {/* Channel selector */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Channel</label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => (
                <button
                  key={ch.value}
                  type="button"
                  onClick={() => setChannel(ch.value)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                    channel === ch.value
                      ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                      : 'border border-orange-200 text-orange-700 hover:bg-orange-50'
                  }`}
                >
                  {ch.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-400">{selectedChannel?.hint}</p>
          </div>

          {/* Campaign name */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Campaign Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. August Re-engagement"
              className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              required
            />
          </div>

          {/* Subject (email only) */}
          {channel === 'EMAIL' && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Email Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. 🍽️ New dining events near you!"
                className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
          )}

          {/* Title (FCM only) */}
          {channel === 'FCM' && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Notification Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. New event for {name}!"
                className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
          )}

          {/* Target City */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Target City <span className="normal-case font-normal text-gray-400">(optional — leave blank for all cities)</span></label>
            <input
              value={targetCity}
              onChange={(e) => setTargetCity(e.target.value)}
              placeholder="e.g. Mumbai"
              className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>

          {/* Body */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              {channel === 'WHATSAPP' ? 'Template Body (maps to {{1}})' : 'Message Body'}
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder={
                channel === 'EMAIL'
                  ? 'Hi {name},\n\nWe have exciting new dining events in your city …'
                  : channel === 'FCM'
                  ? 'Hi {name}, a new event is waiting for you! Open Co-Eater to join.'
                  : 'Hi {name}, check out the latest dining events on Co-Eater!'
              }
              className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-y"
              required
            />
          </div>

          {formError   && <p className="text-sm font-medium text-red-600">{formError}</p>}
          {formSuccess && <p className="text-sm font-medium text-green-600">{formSuccess}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-6 py-2.5 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60 transition"
          >
            {submitting ? 'Queueing…' : '🚀 Send Campaign'}
          </button>
        </form>
      </div>

      {/* ── Campaign List ── */}
      <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="title-font text-xl font-bold text-gray-900">Campaign History</h2>
          <button
            onClick={loadCampaigns}
            disabled={listLoading}
            className="rounded-full border border-orange-200 px-4 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 disabled:opacity-60"
          >
            {listLoading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>

        {listError && <p className="mt-3 text-sm text-red-600">{listError}</p>}

        {!listLoading && campaigns.length === 0 && (
          <p className="mt-4 text-sm text-gray-400">No campaigns yet.</p>
        )}

        <div className="mt-4 space-y-3">
          {campaigns.map((c) => <CampaignRow key={c.id} campaign={c} />)}
        </div>
      </div>
    </div>
  )
}
